import { DatePicker } from "@mui/lab"
import { Checkbox, Grid, ListItemText, Stack, TextField } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"
import { t } from "@lingui/macro"

import { format } from "date-fns"

import { useStore } from "src/store/lib/useStore"
import { CommunitiesOverviewViewStore } from "src/views/community-overview/store"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"

import { formatCommunityNames } from "src/views/community-overview/helpers/communityHelpers"

export const CommunityOverviewHeader = observer(() => {
    const store = useStore(CommunitiesOverviewViewStore)

    const handleDateRangeChange = useCallback(
        (value: Date) => {
            trackModuleEvent("Month picker - community overview", {
                [MixpanelProperties.MonthSelected]: format(value, "yyyy-MM"),
                [MixpanelProperties.EventDefinition]: "Month/year picker",
            })
            return store.setDateRangeFilter(value)
        },
        [store],
    )

    const handleFilterBySelectedCommunity = useCallback(
        (selectedCommunities: IGenericAutoCompleteTextFieldValue[]) => {
            if (selectedCommunities.length > 0) {
                trackModuleEvent("Community filter - overview", {
                    [MixpanelProperties.CommunityNameFilter]:
                        formatCommunityNames(selectedCommunities),
                    [MixpanelProperties.CommunityCount]:
                        selectedCommunities.length,
                })
            }

            store.filterBySelectedCommunity(selectedCommunities)
        },
        [store],
    )

    const renderOptions = useCallback((props, option, state) => {
        if (option === null) return null

        return (
            <Stack direction="row" spacing={1} key={option.id} {...props}>
                <Checkbox checked={state.selected} style={{ marginRight: 8 }} />
                <ListItemText
                    primary={option.label ?? ""}
                    data-testid={`option-${option.id}`}
                />
            </Stack>
        )
    }, [])

    const handleAutoCompleteChange = useCallback(
        (_, value) => {
            // Ensure value is always an array
            const selectedValues = Array.isArray(value) ? value : []
            handleFilterBySelectedCommunity(selectedValues)
        },
        [handleFilterBySelectedCommunity],
    )

    // Convert store's community options to AutoCompleteTextField format
    const communityOptions = useMemo(() => {
        return store.communitiesForMultiSelect
    }, [store.communitiesForMultiSelect])

    // Convert selected community IDs to AutoCompleteTextField option format
    const selectedCommunityOptions = useMemo(() => {
        return communityOptions.filter((option) =>
            store.selectedCommunities.some(
                (id) => id === parseInt(option.id.toString()),
            ),
        )
    }, [store.selectedCommunities, communityOptions])

    return (
        <Grid
            container
            sx={{ paddingX: 1, paddingY: 1, width: "auto", display: "flex" }}
        >
            <Grid
                item
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                }}
            >
                <Stack
                    direction="row"
                    sx={{
                        width: {
                            xs: "100%",
                            sm: "260px",
                        },
                        spacing: 1.25,
                    }}
                >
                    <DatePicker
                        views={["year", "month"]}
                        label={t`community-overview.date-range-header.year-month`}
                        minDate={new Date("2012-03-01")}
                        value={store.startDate}
                        onChange={(date) =>
                            date !== null && handleDateRangeChange(date)
                        }
                        renderInput={(params) => (
                            <TextField {...params} helperText={null} />
                        )}
                    />
                </Stack>
                <AutoCompleteTextField
                    label={t`community-view.header`}
                    options={communityOptions}
                    value={selectedCommunityOptions}
                    onChange={handleAutoCompleteChange}
                    multiple
                    renderOption={renderOptions}
                    disableCloseOnSelect
                    limitTags={1}
                    sx={{ m: 1, width: 280 }}
                />
            </Grid>
        </Grid>
    )
})
